import React, { useEffect, useCallback, useState } from "react";

import { useReviews } from "~/hooks/useReviews";
import { useReviewContext } from "~/hooks/useReviewSnippet";

import ReviewsScoreList from "./ReviewsScoreList";
import ReviewsScoreSummary from "./ReviewsScoreSummary";
import QuestionsItem from "./QuestionsItem";
import { Sheet } from "../Sheet";

import {
  FormInput,
  FormRangeSlider,
  FormSelect,
  IconSearch,
  PaginationNumbered,
  ReviewCard,
  TabbedSection,
} from "@bared/ui";
import AddNewQuestion from "./AddNewQuestion";
import { useRichText } from "@hooks/useRichText";
import { useProduct } from "@/templates/product/useProduct";

export type ReviewsSheetProps = {
  productName: string;
  productHandle: string;
  gender: "Mens" | "Womens" | null;
  productGroup: string;
  sku: string;
  siblingColours?: {
    siblingColour: string;
    siblingHandle: string;
    image: string;
  }[];
  isOpen: boolean;
  handleSheetToggle: () => void;
  initialReviewFilter?: number;
  onReviewFilterChange?: (filter: number) => void;
};

export function ReviewsSheet({
  productName,
  productHandle,
  gender,
  siblingColours,
  productGroup,
  sku,
  isOpen,
  handleSheetToggle,
  initialReviewFilter = 0,
  onReviewFilterChange,
}: ReviewsSheetProps) {
  const { renderRatingSnippet } = useReviewContext();
  const { parseContent } = useRichText();
  const {
    template: { sheetFooterContent },
  } = useProduct();

  const {
    averageScore,
    reviews,
    questions,
    starDistribution,
    totalQuestions,
    totalQuestionPageCount,
    totalReviews,
    totalPageCount,
    totalResultsCount,
    fitFeedbackStatistic,
    reviewsSortOptions,
    questionsSortOptions,
    reviewsPerPage,
    questionsPerPage,
    loading,
    reviewFilter,
    tabs,
    page,
    questionPageCursor,
    handleClearReviewFilter,
    handleClickReviewFilter,
    handleLoadMoreQuestions,
    handleLoadMoreReviews,
    handleQuestionsSortOptionChange,
    handleReviewsSortOptionChange,
    handleSearch,
    handleSearchIconClick,
    handleTabChange,
  } = useReviews({
    productGroup,
    sku,
    isReviewsSheetOpen: isOpen,
    initialReviewFilter,
  });

  const footerContent = parseContent(sheetFooterContent);
  const [activeTab, setActiveTab] = useState<string>(
    tabs.find((tab) => tab.handle === "reviews")?.handle ?? "",
  );

  // Wrap filter handlers to notify parent
  const handleReviewFilterClick = useCallback(
    (filter: number) => {
      handleClickReviewFilter(filter);
      onReviewFilterChange?.(filter);
    },
    [handleClickReviewFilter, onReviewFilterChange],
  );

  const handleReviewFilterClear = useCallback(() => {
    handleClearReviewFilter();
    onReviewFilterChange?.(0);
  }, [handleClearReviewFilter, onReviewFilterChange]);

  // Ensure body overflow stays hidden while this sheet is open
  // This prevents child sheets (like AddNewQuestion) from removing it when they close
  useEffect(() => {
    if (!isOpen) return;

    // Set initial overflow
    document.body.style.overflow = "hidden";

    // Watch for changes to body.style and immediately re-apply overflow:hidden if removed
    const observer = new MutationObserver(() => {
      if (document.body.style.overflow !== "hidden") {
        document.body.style.overflow = "hidden";
      }
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["style"],
    });

    return () => {
      observer.disconnect();
    };
  }, [isOpen]);

  // Reset to reviews tab when sheet opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab("reviews");
    }
  }, [isOpen]);

  // Trigger rating snippet rendering when conditions are met
  useEffect(() => {
    if (!isOpen || loading || totalReviews === 0 || !renderRatingSnippet) {
      return;
    }

    // Wait for DOM to be ready, then trigger the widget
    // Using a longer timeout to ensure the sheet animation completes
    const timer = setTimeout(() => {
      renderRatingSnippet();
    }, 500);

    return () => clearTimeout(timer);
  }, [isOpen, loading, totalReviews, renderRatingSnippet]);

  return (
    <Sheet
      isOpen={isOpen}
      onClose={handleSheetToggle}
      title={`${productName} ${activeTab}`}
      eventTitle="Reviews & Questions Sheet"
      className="px-mini pb-midi md:px-maxi"
      hideMode="css"
    >
      {(totalReviews > 0 || totalQuestions > 0) && (
        <div className="relative" id="reviews-questions-container">
          <TabbedSection
            stickyTabTitle
            activeTab={activeTab}
            tabs={tabs}
            onChange={(tabHandle) => {
              setActiveTab(tabHandle);
              handleTabChange();
            }}
          >
            {(tabhandle) => {
              if ("reviews" === tabhandle) {
                return (
                  <div>
                    <div className="flex justify-between">
                      <ReviewsScoreSummary
                        averageScore={averageScore}
                        productGroup={productGroup}
                      />
                    </div>

                    <div className="space-y-midi border-b-[1px] border-b-primary-300 py-midi">
                      <div className="flex flex-col justify-between space-y-midi md:flex-row md:space-y-zero">
                        <ReviewsScoreList
                          className="md:min-w-[330px] md:max-w-[330px]"
                          reviewFilter={reviewFilter}
                          totalReviews={totalReviews}
                          starDistribution={starDistribution}
                          handleClick={handleReviewFilterClick}
                          handleClear={handleReviewFilterClear}
                        />
                        {gender === "Womens" || gender === "Mens" ? (
                          <div className="flex w-full flex-col space-y-maxi sm:min-w-[330px] sm:max-w-[330px]">
                            {fitFeedbackStatistic.map((stats) => {
                              return (
                                <div className="flex flex-col space-y-mini">
                                  <p className="text-caption font-medium leading-caption">
                                    {stats.title}:
                                  </p>
                                  <FormRangeSlider
                                    rangeOption={stats.rangeOptions}
                                    value={stats.averageRating}
                                    min={1}
                                    max={5}
                                    step={0.01}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        ) : null}
                      </div>

                      <div className="flex flex-col space-y-mini md:flex-row md:items-center md:justify-between md:space-y-zero">
                        <div className="flex space-x-mini">
                          <div className="relative w-1/2 md:min-w-[215px]">
                            <FormInput
                              id="search-keyword"
                              placeholder="Search Reviews"
                              className="h-[41px] border pl-micro pr-maxi text-caption placeholder-primary-600 sm:pl-mini"
                              onKeyDown={handleSearch}
                            />
                            <button
                              className="absolute right-zero top-zero flex h-[40px] w-[30px] cursor-pointer items-center justify-center"
                              onClick={handleSearchIconClick}
                            >
                              <IconSearch className="w-mini" />
                            </button>
                          </div>

                          <div className="w-1/2 md:min-w-[215px]">
                            <FormSelect
                              className="border text-caption"
                              placeholder=""
                              defaultValue={reviewsSortOptions[0].value}
                              options={reviewsSortOptions}
                              onChange={handleReviewsSortOptionChange}
                            />
                          </div>
                        </div>

                        <p className="text-right text-caption leading-caption">
                          {reviewsPerPage} of {totalResultsCount} Reviews
                        </p>
                      </div>
                    </div>

                    <div>
                      <div>
                        {loading ? (
                          <p className="pt-midi text-body leading-hint">
                            Loading ...
                          </p>
                        ) : (
                          reviews.map(
                            (
                              {
                                id,
                                rating,
                                title,
                                comments,
                                author: { name, location },
                                productName,
                                timeAgo,
                                replies,
                              },
                              index,
                            ) => {
                              const shoeSize =
                                productName.split(" / ")[1] || undefined;

                              const styleName = siblingColours
                                ?.filter(({ siblingColour }) => {
                                  const regex = /-/g;
                                  return !siblingColour.match(regex);
                                })
                                .find(({ siblingColour }) =>
                                  productName.includes(siblingColour),
                                );

                              const emptyLocation = location.replace('""', "");
                              const country =
                                location.split(",").length > 1
                                  ? location.split(",")[1]
                                  : location;

                              return (
                                <ReviewCard
                                  key={id}
                                  userName={name}
                                  country={
                                    !!emptyLocation ? country : undefined
                                  }
                                  shoeHandle={styleName?.siblingHandle}
                                  shoeImage={styleName?.image}
                                  shoeStyle={styleName?.siblingColour}
                                  shoeSize={shoeSize}
                                  rating={rating}
                                  timeAgo={timeAgo}
                                  reviewTitle={title}
                                  review={comments}
                                  response={replies?.[0]?.comments}
                                  isFirst={index === 0}
                                />
                              );
                            },
                          )
                        )}

                        {!loading &&
                        totalReviews !== 0 &&
                        reviews.length === 0 ? (
                          <p className="pt-midi text-body leading-hint">{`No ${reviewFilter ? `${reviewFilter}-star ` : ""}reviews found. `}</p>
                        ) : null}
                      </div>

                      {reviews.length !== 0 && (
                        <PaginationNumbered
                          currentPage={page}
                          className="justify-end"
                          totalPages={totalPageCount}
                          onChange={(page) =>
                            handleLoadMoreReviews({ selected: page - 1 })
                          }
                        />
                      )}
                    </div>
                  </div>
                );
              }

              if ("questions" === tabhandle) {
                return totalQuestions > 0 ? (
                  <div className="pt-mini">
                    <div className="flex w-full flex-col items-start justify-between space-y-midi md:flex-row md:items-center md:space-y-zero">
                      <AddNewQuestion
                        sku={sku}
                        productHandle={productHandle}
                        productName={productName}
                        closeReviewsSheet={handleSheetToggle}
                      />

                      <div className="flex w-full items-center space-x-mini">
                        <div className="w-1/2 md:max-w-[215px]">
                          <FormSelect
                            className="border text-caption"
                            placeholder=""
                            defaultValue={questionsSortOptions[0].value}
                            options={questionsSortOptions}
                            onChange={handleQuestionsSortOptionChange}
                          />
                        </div>

                        <p className="text-caption leading-caption">
                          {questionsPerPage} of {totalQuestions} Questions
                        </p>
                      </div>
                    </div>

                    <div>
                      <div>
                        {loading ? (
                          <p className="text-body leading-hint">Loading ...</p>
                        ) : (
                          questions.map(
                            ({
                              id,
                              author: { name },
                              comments,
                              timeAgo,
                              replies,
                            }) => {
                              return (
                                <QuestionsItem
                                  className="last:mb-4 border-b-[1px] border-b-primary-300 py-midi"
                                  key={id}
                                  userName={name}
                                  question={comments}
                                  timeAgo={timeAgo}
                                  responses={replies}
                                />
                              );
                            },
                          )
                        )}
                      </div>

                      {questions.length !== 0 && (
                        <PaginationNumbered
                          currentPage={questionPageCursor}
                          className="justify-end"
                          totalPages={totalQuestionPageCount}
                          onChange={(questionPageCursor) =>
                            handleLoadMoreQuestions({
                              selected: questionPageCursor - 1,
                            })
                          }
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex w-full flex-col items-start justify-between space-y-micro md:flex-row md:items-center md:space-y-zero">
                    <p className="text-body">Ask us anything.</p>
                    <AddNewQuestion
                      sku={sku}
                      productHandle={productHandle}
                      productName={productName}
                      closeReviewsSheet={handleSheetToggle}
                    />
                  </div>
                );
              }
            }}
          </TabbedSection>

          {footerContent && (
            <div className="mt-maxi border-t border-primary-300 pt-midi">
              {footerContent}
            </div>
          )}
        </div>
      )}
    </Sheet>
  );
}
